import { Search, Bell, Calendar, ChevronDown, Trash2, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';
import { useGlobalFilter, TIME_RANGE_LABELS, type GlobalTimeRange } from '@/contexts/GlobalFilterContext';
import { useAuth } from '@/contexts/AuthContext';
import { getSyncStatus, onSyncStatusChange, syncNow, type SyncStatus } from '@/lib/syncEngine';
import { TrashDialog } from './TrashDialog';

interface TopBarProps {
  title: string;
}

/** 相对时间："刚刚 / X 秒前 / X 分钟前 / HH:MM" */
function formatAgo(ts: number): string {
  if (!ts) return '从未同步';
  const diff = Math.max(0, Date.now() - ts);
  if (diff < 10_000) return '刚刚';
  if (diff < 60_000) return `${Math.floor(diff / 1000)} 秒前`;
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** #789 同步状态徽章：消费 syncEngine 的 syncing / lastSyncAt / lastError，秒级感知云端健康 */
function SyncStatusBadge() {
  const [status, setStatus] = useState<SyncStatus>(() => getSyncStatus());
  useEffect(() => {
    const off = onSyncStatusChange(() => setStatus(getSyncStatus()));
    const timer = setInterval(() => setStatus(getSyncStatus()), 5000); // 相对时间滚动刷新
    return () => { off(); clearInterval(timer); };
  }, []);

  if (!status.configured || !status.enabled) return null; // 未启用同步不占位

  // 同步中
  if (status.syncing) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 h-8 rounded-lg bg-sky-50 text-sky-600 border border-sky-100 text-[12px] font-medium" title="正在与云端同步">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        同步中
      </div>
    );
  }
  // 失败（红色 ⚠️）
  if (status.lastError) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 h-8 rounded-lg bg-red-50 text-red-600 border border-red-100 text-[12px] font-medium" title={`上次同步失败：${status.lastError}`}>
        <AlertTriangle className="w-3.5 h-3.5" />
        同步失败
      </div>
    );
  }
  // 成功
  return (
    <div className="flex items-center gap-1.5 px-2.5 h-8 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100 text-[12px] font-medium" title={`上次同步 ${formatAgo(status.lastSyncAt)}`}>
      <CheckCircle2 className="w-3.5 h-3.5" />
      {formatAgo(status.lastSyncAt)}已同步
    </div>
  );
}

/** 顶部安全同步入口：只执行常规的拉取后推送，不触发强制全量拉取。 */
export function SyncNowButton({ compact = false }: { compact?: boolean }) {
  const [status, setStatus] = useState<SyncStatus>(() => getSyncStatus());
  const [feedback, setFeedback] = useState<'success' | 'error' | null>(null);
  const feedbackTimer = useRef<number | null>(null);

  useEffect(() => {
    const off = onSyncStatusChange(() => setStatus(getSyncStatus()));
    return () => {
      off();
      if (feedbackTimer.current !== null) window.clearTimeout(feedbackTimer.current);
    };
  }, []);

  const showFeedback = (next: 'success' | 'error') => {
    setFeedback(next);
    if (feedbackTimer.current !== null) window.clearTimeout(feedbackTimer.current);
    feedbackTimer.current = window.setTimeout(() => setFeedback(null), 3000);
  };

  const handleSync = async () => {
    if (status.syncing || !status.configured || !status.enabled) return;
    const result = await syncNow('topbar-manual');
    showFeedback(result.ok ? 'success' : 'error');
  };

  const syncing = status.syncing;
  const unavailable = !status.configured || !status.enabled;
  const label = syncing ? '同步中' : feedback === 'success' ? '已同步' : feedback === 'error' ? '同步失败' : '立即同步';
  const title = unavailable
    ? '请先在系统设置中启用多设备同步'
    : feedback === 'error'
      ? `同步失败：${status.lastError || '请稍后重试'}`
      : '立即同步（拉取 + 推送）';
  const Icon = syncing ? RefreshCw : feedback === 'success' ? CheckCircle2 : feedback === 'error' ? AlertTriangle : RefreshCw;

  return (
    <Button
      variant={compact ? 'ghost' : 'outline'}
      size={compact ? 'icon' : 'sm'}
      onClick={() => void handleSync()}
      disabled={syncing || unavailable}
      title={title}
      aria-label={label}
      className={cn(
        compact
          ? 'h-9 w-9 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700'
          : 'h-8 px-2.5 gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800',
        feedback === 'error' && 'border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700'
      )}
    >
      <Icon className={cn(compact ? 'w-4 h-4' : 'w-3.5 h-3.5', syncing && 'animate-spin')} />
      {!compact && <span className="text-[12.5px]">{label}</span>}
    </Button>
  );
}

const TIME_OPTIONS: { key: GlobalTimeRange; label: string }[] = [
  { key: 'today', label: '今日' },
  { key: 'yesterday', label: '昨日' },
  { key: 'week', label: '本周' },
  { key: 'month', label: '本月' },
  { key: 'quarter', label: '本季度' },
  { key: 'year', label: '本年' },
  { key: 'custom', label: '自定义' },
];

export function TopBar({ title }: TopBarProps) {
  const {
    searchQuery, setSearchQuery,
    timeRange, setTimeRange,
    customStartDate, setCustomStartDate,
    customEndDate, setCustomEndDate,
  } = useGlobalFilter();
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const { user } = useAuth();
  // #788 头像跟随登录用户名首字（与 Sidebar 一致）
  const displayName = user?.displayName || user?.username || '未';
  const avatarChar = displayName.slice(0, 1);

  const currentLabel = TIME_RANGE_LABELS[timeRange];
  const customLabel = timeRange === 'custom' && (customStartDate || customEndDate)
    ? `${customStartDate || '开始'} ~ ${customEndDate || '结束'}`
    : currentLabel;

  return (
    <header className="hidden xl:flex items-center justify-between px-5 py-2.5 bg-white border-b border-zinc-200 flex-shrink-0 relative z-[110]">
      <div className="flex items-center gap-3">
        <h1 className="text-[15px] font-semibold text-zinc-900 tracking-tight">{title}</h1>
        <span className="text-zinc-300">/</span>
        <span className="text-[12.5px] text-zinc-500">昌发香行</span>
      </div>

      <div className="flex items-center gap-3">
        <SyncNowButton />
        {/* #789 云端同步状态（成功绿色 / 失败红色 / 同步中旋转） */}
        <SyncStatusBadge />

        {/* Global search — 联动全页面数据 */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索客户、商品、订单..."
            className="w-56 bg-zinc-100 rounded-lg pl-8 pr-3 py-1.5 text-[12.5px] text-zinc-800 placeholder:text-zinc-400 outline-none focus:ring-2 focus:ring-emerald-500/20 focus:bg-white transition"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            >
              ×
            </button>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowTrash(true)}
          className="relative h-8 px-2 text-zinc-500 hover:text-emerald-600 hover:bg-emerald-50 gap-1.5"
          title="回收站（已删除数据可恢复）"
        >
          <Trash2 className="w-4 h-4" />
          <span className="text-[12.5px]">回收站</span>
        </Button>

        <Button variant="ghost" size="icon" className="relative h-8 w-8 text-zinc-500 hover:text-zinc-900">
          <Bell className="w-4 h-4" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-emerald-500 rounded-full ring-2 ring-white" />
        </Button>

        {/* Global time selector — 联动全页面数据范围 */}
        <div className="relative">
          <div className="hidden xl:flex items-center gap-1 bg-white border border-zinc-200 rounded-lg p-1 shadow-sm">
            <Calendar className="w-3.5 h-3.5 text-emerald-600 ml-2" />
            {TIME_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setTimeRange(opt.key)}
                className={cn(
                  'px-3 py-1.5 text-[12px] font-medium rounded-md transition-all',
                  timeRange === opt.key
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-zinc-600 hover:bg-emerald-50 hover:text-emerald-700'
                )}
              >
                {opt.label}
              </button>
            ))}
            {timeRange === 'custom' && (
              <div className="flex items-center gap-1.5 pl-2 pr-1 border-l border-zinc-100">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => { setCustomStartDate(e.target.value); if (!customEndDate) setCustomEndDate(e.target.value); }}
                  className="h-7 px-1.5 text-[11px] border border-zinc-200 rounded-md outline-none focus:border-emerald-500 text-zinc-700"
                />
                <span className="text-zinc-400 text-[11px]">~</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="h-7 px-1.5 text-[11px] border border-zinc-200 rounded-md outline-none focus:border-emerald-500 text-zinc-700"
                />
              </div>
            )}
          </div>
          {/* Compact dropdown for smaller desktop */}
          <div className="xl:hidden">
            <button
              onClick={() => setShowTimeDropdown(!showTimeDropdown)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-200 rounded-lg text-[12.5px] font-medium text-emerald-700 shadow-sm hover:bg-emerald-50 transition-colors"
            >
              <Calendar className="w-3.5 h-3.5 text-emerald-600" />
              {customLabel}
              <ChevronDown className={cn('w-3 h-3 text-emerald-600 transition-transform', showTimeDropdown && 'rotate-180')} />
            </button>
            {showTimeDropdown && (
              <div className="absolute top-full right-0 mt-1.5 bg-white border border-zinc-200 rounded-lg shadow-lg z-50 py-1 min-w-[180px]">
                {TIME_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => { setTimeRange(opt.key); }}
                    className={cn(
                      'w-full text-left px-3 py-1.5 text-[12.5px] transition-colors',
                      timeRange === opt.key ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-zinc-600 hover:bg-zinc-50'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
                {timeRange === 'custom' && (
                  <div className="px-3 py-2 border-t border-zinc-100 flex flex-col gap-1.5">
                    <label className="text-[11px] text-zinc-500">开始日期</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => { setCustomStartDate(e.target.value); if (!customEndDate) setCustomEndDate(e.target.value); }}
                      className="w-full h-8 px-2 text-[12px] border border-zinc-200 rounded-md outline-none focus:border-emerald-500 text-zinc-700"
                    />
                    <label className="text-[11px] text-zinc-500 mt-0.5">结束日期</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full h-8 px-2 text-[12px] border border-zinc-200 rounded-md outline-none focus:border-emerald-500 text-zinc-700"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          title={displayName}
          className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center text-white text-[11px] font-medium cursor-pointer"
        >
          {avatarChar}
        </div>
      </div>
      {showTrash && <TrashDialog onClose={() => setShowTrash(false)} />}
    </header>
  );
}
